import { createStore } from "vuex";

export default class {
  constructor() {
    const state = {
      activated: true,
      loading: true,
      error: false,
      errorMessages: ["test"],
      submitFail: false,
      logo: null,
      data: null,
      submit: null,
      partialSubmit: null,
      formCompletionIndex: process.env.NODE_ENV === "development" ? 100 : 0,
      model: {},
    };

    const getters = {
      activated: ({ activated }) => activated,
      ready: ({ loading, error }) => !loading && !error,
      title: ({ data }) => data.title || "",
      logo: ({ logo, data }) => [logo || data.logo || "", data.logoLink || ""],
      exitModal: ({ data }) => data.exitModal || "",
      redirects: ({ data }) => data.redirects || [],
      submitEndpoint: ({ submit }) => submit || "",
      partialSubmitEndpoint: ({ partialSubmit }) => partialSubmit || "",
      model: ({ model }) => model,
      formCompletionIndex: ({ formCompletionIndex }) => formCompletionIndex,
      nav: ({ data }) => {
        if (!data) return false;
        return state.data.pages.map((p) => ({ id: p.slug, label: p.navLabel }));
      },
      pages: ({ data }) => data.pages,
      page:
        ({ data }) =>
        (slug) =>
          data.pages.find((p) => p.slug === slug),
      prevPage: (state) => (currentSlug) => {
        const index =
          state.data.pages.findIndex((p) => p.slug === currentSlug) - 1;
        const slug = state.data.pages[index].slug;
        return { slug, index };
      },
      nextPage: (state) => (currentSlug) => {
        const index =
          state.data.pages.findIndex((p) => p.slug === currentSlug) + 1;
        const slug = state.data.pages[index].slug;
        return { slug, index };
      },
    };

    const mutations = {
      assignEndpoints(state, payload) {
        state.submit = payload[0];
        state.partialSubmit = payload[1];
      },
      setLogo(state, payload) {
        state.logo = payload;
      },
      setFormCompletionIndex(state, payload) {
        if (payload > state.formCompletionIndex) {
          state.formCompletionIndex = payload;
        }
      },
      setActivation(state, payload) {
        state.activated = payload !== null && payload !== "";

        if (payload && Object.keys(state.model).length == 0) {
          try {
            const model = JSON.parse(payload);

            state.model = model;
          } catch (e) {
            return;
          }
        }
      },
      setLoading(state, payload) {
        state.loading = payload;
      },
      setError(state, payload) {
        state.error = payload;
      },
      setSubmitFail(state, payload) {
        state.submitFail = payload;
      },
      loadData(state, payload) {
        payload.pages.forEach((page, i) => (page.step = i + 1));
        state.data = payload;
      },
      pushLastFieldToDataLayer(_, payload) {
        //console.log("DATALAYER -> formLastField: " + payload);
        window.dataLayer.push({ formLastField: payload });
      },
      updateModel(state, payload) {
        if (!state.model[payload.id]) {
          if (payload.index !== undefined) {
            let value = [];
            value[payload.index] = payload.value;
            state.model = { ...state.model, [payload.id]: value };
          } else {
            state.model = { ...state.model, [payload.id]: payload.value };
          }
        } else {
          if (payload.index !== undefined) {
            state.model[payload.id][payload.index] = payload.value;
          } else {
            state.model[payload.id] = payload.value;
          }
        }
      },
      deleteModelEntry(state, payload) {
        delete state.model[payload];
      },
    };

    const actions = {
      submitData({ commit, state: model }, payload) {
        return new Promise((resolve) => {
          function getFormData(model) {
            const formData = new FormData();

            // get elements from form
            for (const key in model) {
              if (!Array.isArray(model[key])) {
                formData.append(key, model[key]);
              } else {
                for (let i = 0; i < model[key].length; i++) {
                  formData.append(key + "-" + i, model[key][i]);
                }
              }
            }

            // get gclid
            const gclidParamName = "gclid";
            const urlParams = new URLSearchParams(window.location.search);
            const gclidValue = urlParams.has(gclidParamName)
              ? urlParams.get(gclidParamName)
              : "";
            formData.append("gclid", gclidValue);
            formData.append("form_type", "short");

            return formData;
          }

          const formData = getFormData(model.model);

          fetch(payload, {
            method: "POST",
            body: formData,
          })
            .then((response) => {
              if (response.ok) {
                return response.json();
              } else {
                return "Fail";
              }
            })
            .then((results) => {
              resolve(results);
              return;
            })
            .catch(() => {
              commit("setError", true);
              resolve(false);
              return "Fail";
            });
        });
      },
      fetchData(
        { commit, getters },
        { attrs: { source, formId }, query, router }
      ) {
        return new Promise((resolve) => {
          const triggers = [
            ...document.querySelectorAll(`[data-trigger-form="${formId}"]`),
          ];
          const activate = (event) => {
            const model =
              event &&
              event.target &&
              event.target.dataset &&
              event.target.dataset.model;

            commit("setActivation", model || {});
            router.push(getters.pages[0].slug);
            resolve();
          };

          fetch(source)
            .then((response) => {
              if (response.ok) {
                return response.json();
              } else {
                return false;
              }
            })
            .then((data) => {
              if (data) {
                commit("loadData", data);
              } else {
                commit("setError", true);
              }
            })
            .catch(() => {
              commit("setError", true);
            })
            .finally(() => {
              triggers.forEach((t) => t.addEventListener("click", activate));

              document.addEventListener("vueform.trigger", function (event) {
                if (event.detail == formId) {
                  activate();
                }
              });

              if (process.env.NODE_ENV === "development") activate();
              else if (query.form === formId) activate();

              commit("setLoading", false);
            });
        });
      },
    };
    const modules = {};

    this.store = createStore({
      state,
      getters,
      mutations,
      actions,
      modules,
    });

    return this.store;
  }
}
